import { Link } from "@mui/material"
import { Link as NavLink } from "react-router-dom"

export const obj1 = {
  title: "1. Information We Collect",
  subTitle: "We collect several types of information to provide and improve our emotional wellness platform:",
  list: [
    {
      key: "Personal Identification Data:",
      summary: "Full name, email address, phone number, mailing address, and profile photo.",
    },
    {
      key: "Account Credentials:",
      summary: "Username, encrypted password, role designation (Member or Specialist), and account preferences.",
    },
    {
      key: "Wellness Questionnaire & Assessment Data:",
      summary: "Non-clinical wellness goals, preferences, self-reported interests, and feedback provided voluntarily during registration or through questionnaires.",
    },
    {
      key: "Payment & Billing Metadata:",
      summary: "Billing address and transaction history. Credit card details are collected and processed directly by our PCI-compliant payment processor (Stripe) and are never stored on InnerPal servers.",
    },
    {
      key: "Technical & Device Data:",
      summary: "IP address, browser type, operating system, device identifiers, site usage analytics, and cookie data.",
    },
  ]
}

export const obj2 = {
  title: "2. How We Use Your Information",
  subTitle: "We use your personal data for legitimate business purposes, including:",
  list: [
    { summary: "Creating, managing, and authenticating your InnerPal account" },
    { summary: "Facilitating 1-on-1 session bookings and webinar registrations with independent Specialists" },
    { summary: "Personalizing self-guided courses, wellness plans, and platform recommendations" },
    { summary: "Processing subscription fees and appointment payments" },
    { summary: "Sending transactional messages, session reminders, invoices, and system notifications" },
    { summary: "Providing customer support and responding to user inquiries" },
    { summary: "Improving platform security, preventing fraud, and ensuring acceptable use" },
    { summary: "Sending news, updates, and promotional communications (you may opt out at any time)" },
  ]
}

export const obj3 = {
  title: "3. Sharing Your Information",
  subTitle: "We strictly respect your privacy. We share your information only in the following necessary contexts:",
  otherTitle: "We NEVER sell, rent, or trade your personal data to third parties for commercial gain.",
  list: [
    {
      key: "Specialists You Engage With:",
      summary: "When you book a session, essential profile information is shared with the relevant Specialist to facilitate your appointment.",
    },
    {
      key: "Service Providers:",
      summary: "Trusted third-party vendors who assist in operating our platform (e.g., Stripe for payments, cloud hosting infrastructure, email delivery services).",
    },
    {
      key: "Analytics & Growth Partners:",
      summary: "Service providers such as Google Analytics to monitor platform usage and performance.",
    },
    {
      key: "Legal Requirements:",
      summary: "Disclosures required to comply with court orders, applicable laws, legal processes, or to protect the safety and rights of InnerPal users.",
    },
  ]
}

export const obj4 = {
  title: "4. Cookies and Tracking Technologies",
  subTitle:
    <>
      We utilize cookies, local storage, and tracking scripts to optimize navigation, remember preferences, and analyze web traffic. For full details on cookie categories and management options, please review our <Link
        target="_blank"
        component={NavLink}
        to="/cookie-policy"
      >Cookie Policy
      </Link>.
    </>,
}

export const obj5 = {
  title: "5. Data Security",
  subTitle: "We employ industry-standard technical, administrative, and physical safeguards to protect your personal information against unauthorized access, loss, alteration, or disclosure. This includes SSL/TLS encryption for data transmission and restricted access database controls. However, no internet transmission is 100% secure, and you use the Platform at your own risk.",
}

export const obj6 = {
  title: "6. Your Data Rights (GDPR / CCPA)",
  subTitle: "Depending on your geographical location, you have statutory rights regarding your personal data:",
  otherTitle:
    <>
      To exercise any of these rights, please contact our privacy coordinator at [<Link
        target="_blank"
        component={NavLink}
        to="mailto:support@innerpal.com"
      >support@innerpal.com
      </Link>].
    </>,
  list: [
    { key: "Right of Access:", summary: "Request a copy of the personal data we hold about you." },
    { key: "Right to Rectification:", summary: "Request correction of incomplete or inaccurate data." },
    { key: "Right to Erasure (\"Right to be Forgotten\"):", summary: "Request deletion of your personal account and associated data." },
    { key: "Right to Restrict Processing:", summary: "Request limitations on how we process your data." },
    { key: "Right to Data Portability:", summary: "Request transfer of your data to another provider in a structured, machine-readable format." },
    { key: "Right to Withdraw Consent:", summary: "Revoke consent for optional data processing at any time." },
  ]
}

export const obj7 = {
  title: "7. Data Retention",
  subTitle: "We retain your personal data only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, comply with legal and accounting obligations, and resolve disputes. Upon account termination, personal data is securely deleted or anonymized in accordance with our retention schedules.",
}

export const obj8 = {
  title: "8. Children's Privacy",
  subTitle: "InnerPal is strictly intended for individuals who are 18 years of age or older. We do not knowingly collect or solicit personal data from children or minors under 18. If we become aware that a minor under 18 has submitted personal data, we will promptly delete that information.",
}

export const obj9 = {
  title: "9. International Users & Data Transfers",
  subTitle: "If you access InnerPal from outside the country where our servers are located, your information may be transferred across international borders. We ensure adequate safeguards and standard contractual clauses are implemented to protect your data across borders.",
}

export const obj10 = {
  title: "10. Updates to This Privacy Policy",
  subTitle: "We may update this Privacy Policy periodically to reflect changes in our practices or legal requirements. Updated policies will be posted on this page with a revised \"Effective Date.\"",
}